import { AfterViewInit, Component, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { debounceTime } from 'rxjs';
import {
  formatarCpfCnpj,
  formatarTelefone
} from 'src/app/core/utils/utilities';
import { AuthService } from 'src/app/services/AuthService/auth.service';
import { FuncionarioService } from 'src/app/services/FuncionarioService/funcionario.service';
import { SwalAlertService } from 'src/app/services/SwalAlertService/swalAlert.service';

import { ITabelaFuncionario } from './listagem-funcionario.model';

@Component({
  selector: 'app-listagem-funcionario-empresa',
  templateUrl: './listagem-funcionario.component.html',
  styleUrls: ['./listagem-funcionario.component.scss']
})
export class ListagemFuncionarioEmpresaComponent implements OnInit, AfterViewInit {
  isLoading: boolean = false;
  numeroPagina: number = 0;
  isEmployeeLogin: boolean = false;

  form = this.fb.group({
    NM_FUNC: [''],
    QTD_ITEM_LIST: ['10']
  });

  tableData: ITabelaFuncionario[] = [];
  maxPages: number = 1;

  qtdOptions = [
    { name: '5', value: 5 },
    { name: '10', value: 10 },
    { name: '15', value: 15 },
    { name: '20', value: 20 }
  ];

  tableColumns: any[] = [];

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private authService: AuthService,
    private funcionarioService: FuncionarioService,
    private swalAlert: SwalAlertService
  ) {}

  ngOnInit(): void {
    this.isEmployeeLogin = this.authService.getCurrentUserType() === 'FUNC';
    this.tableColumns = this.buildColumns();
    this.getFuncionarios();
  }

  ngAfterViewInit(): void {
    if (this.isEmployeeLogin) {
      return;
    }

    this.form.valueChanges.pipe(debounceTime(300)).subscribe(() => {
      this.getFuncionarios();
    });
  }

  buildColumns(): any[] {
    const columns = [
      { header: 'Código', key: 'codigo', type: 'text' },
      { header: 'Username', key: 'username', type: 'text' },
      { header: 'Nome', key: 'nome', type: 'text' },
      { header: 'CPF', key: 'cpf', type: 'text' },
      { header: 'Telefone', key: 'telefone', type: 'text' },
    //   { header: 'Perfil', key: 'perfil', type: 'text' },
      { header: 'E-mail', key: 'email', type: 'text' }
    ];

    if (this.isEmployeeLogin) {
      return [
        ...columns,
        {
          header: 'Ações',
          key: 'actions',
          type: 'buttons',
          buttons: [
            {
              label: 'Editar',
              img: 'assets/edit.svg',
              action: (item: any) => this.editarFuncionario(item)
            }
          ]
        }
      ];
    }

    return [
      ...columns,
      {
        header: 'Suspender',
        key: 'ativo',
        type: 'checkbox',
        action: (item: any) => this.suspenderFuncionario(item)
      },
      {
        header: 'Ações',
        key: 'actions',
        type: 'buttons',
        buttons: [
          {
            label: 'Editar',
            img: 'assets/edit.svg',
            action: (item: any) => this.editarFuncionario(item),
            condition: (item: any) => this.podeEditarOuExcluir(item)
          },
          {
            label: 'Visualizar',
            img: 'assets/eye-icon.png',
            action: (item: any) => this.visualizarFuncionario(item)
          },
          {
            label: 'Excluir',
            img: 'assets/trashcan.png',
            action: (item: any) => this.excluirFuncionario(item),
            condition: (item: any) => this.podeEditarOuExcluir(item)
          }
        ]
      }
    ];
  }

  navegarCadastroFuncionario() {
    this.router.navigate(['empresa/cadastro/funcionario']);
  }

  escolherQtdLista(value: number): void {
    this.getFuncionarios(1, value);
  }

  mudarPagina(pagina: number) {
    this.numeroPagina = pagina;
    this.getFuncionarios(pagina + 1);
  }

  getFuncionarios(pageNumber?: number, pageSize?: number): void {
    this.isLoading = true;

    if (this.isEmployeeLogin) {
      const idFuncionario = Number(localStorage.getItem('id'));

      this.funcionarioService.GetFuncionarioById(idFuncionario).subscribe({
        next: response => {
          this.tableData = [this.mapTableItem(response)];
          this.maxPages = 1;
          this.isLoading = false;
        },
        error: () => {
          this.tableData = [];
          this.isLoading = false;
        }
      });

      return;
    }

    const nomeFuncionario = String(this.form.controls.NM_FUNC.value || '');
    const qtd = Number(this.form.controls.QTD_ITEM_LIST.value || 10);

    this.funcionarioService
      .GetFuncionarioByFilter(nomeFuncionario, pageNumber, pageSize || qtd)
      .subscribe({
        next: response => {
          this.maxPages = response.TOTAL_PAGES;
          this.tableData = (response.ITEMS || []).map((item: any) =>
            this.mapTableItem(item)
          );
          this.isLoading = false;
        },
        error: () => {
          this.tableData = [];
          this.isLoading = false;
        }
      });
  }

  mapTableItem(item: any): any {
    return {
      codigo: item.PK_FUNC ?? 0,
      username: item.TX_USER_NAME ?? '---',
      nome: item.NM_FUNC ?? '---',
      cpf: formatarCpfCnpj(String(item.TX_CPF_FUNC || ''), 'cpf'),
      telefone: formatarTelefone(String(item.TX_TELE_FUNC || '')),
      perfil: item.NM_PERF ?? '---',
      email: item.TX_MAIL_FUNC ?? '---',
      ativo: item.NU_STTS_FUNC == 1 ? false : true,
      situacao: item.NU_STTS_FUNC ?? 0
    };
  }

  editarFuncionario(item: any) {
    this.router.navigate(['empresa/cadastro/funcionario'], {
      queryParams: {
        codigo: item.codigo,
        modoFuncionario: 'Editar'
      }
    });
  }

  visualizarFuncionario(item: any) {
    this.router.navigate(['empresa/cadastro/funcionario'], {
      queryParams: {
        codigo: item.codigo,
        modoFuncionario: 'Visualizar'
      }
    });
  }

  excluirFuncionario(item: any): void {
    this.swalAlert.showAlert(
      'Atenção!',
      'Deseja realizar a exclusão?',
      'warning',
      true,
      true,
      {
        cancelButtonText: 'Cancelar',
        cancelButtonColor: 'red',
        confirmButtonColor: 'green',
        confirmButtonText: 'Confirmar'
      },
      () => {
        this.funcionarioService.PatchFuncionario(item.codigo, 0).subscribe({
          next: () => {
            this.swalAlert.showAlert('Sucesso!', 'Excluído com sucesso.', 'success');
            this.getFuncionarios(this.numeroPagina + 1);
          }
        });
      }
    );
  }

  suspenderFuncionario(item: any): void {
    const statusFuncionario = item.situacao == 1 ? 2 : 1;

    this.funcionarioService
      .PatchFuncionario(item.codigo, statusFuncionario)
      .subscribe({
        next: () => {
          this.swalAlert.showAlert(
            'Sucesso!',
            `${statusFuncionario == 1 ? 'Remoção da suspensão' : 'Suspensão'} realizada com sucesso.`,
            'success'
          );
          this.getFuncionarios(this.numeroPagina + 1);
        },
        error: (err) => {
          this.swalAlert.handleError(
            err,
            'Erro!',
            'Falha ao alterar o status do funcionário.'
          );
        }
      });
  }

  podeEditarOuExcluir(item: any): boolean {
    return Number(item.codigo) !== Number(localStorage.getItem('id'));
  }
}
